/**
 * Records page views and open-tab time for the PMO Portal Usage dashboard.
 * Time keeps running while the portal tab exists (even under another app).
 * A hidden tab still counts for 15 minutes, then pauses so overnight is not a shift.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../utils/apiClient';
import { resolvePortalPage } from '../utils/portalUsagePages';

const HIDDEN_GRACE_MS = 15 * 60 * 1000;

const newSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function usePortalUsageTracker(projectMenuItem = null) {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const sessionRef = useRef(null);
  const pageRef = useRef(null);

  const page = resolvePortalPage({
    pathname: location.pathname,
    search: location.search,
    state: location.state,
    hash: location.hash,
    projectMenuItem,
  });

  useEffect(() => {
    if (!accounts?.[0] || !page?.pageId) return undefined;

    let cancelled = false;
    let lastTick = Date.now();
    let pendingMs = 0;
    let hiddenAt = typeof document !== 'undefined' && document.visibilityState === 'hidden'
      ? Date.now()
      : null;

    const takeElapsedSeconds = () => {
      const now = Date.now();
      const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
      if (visible) pendingMs += now - lastTick;
      lastTick = now;
      const seconds = Math.floor(pendingMs / 1000);
      pendingMs -= seconds * 1000;
      return seconds;
    };

    const send = async (payload, { elapsedSeconds: forcedElapsed } = {}) => {
      const elapsedSeconds = forcedElapsed != null ? forcedElapsed : takeElapsedSeconds();
      if (cancelled || !accounts[0]) return;
      try {
        await authenticatedApiCall(instance, accounts, 'POST', '/portal-usage/heartbeat', {
          ...payload,
          elapsedSeconds,
          visibleSeconds: elapsedSeconds,
        }, {
          timeout: 8000,
        });
      } catch (err) {
        pendingMs += elapsedSeconds * 1000;
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[portal-usage] heartbeat skipped', err?.message || err);
        }
      }
    };

    const startSession = (nextPage, { resume = false } = {}) => {
      const clientSessionId = resume && sessionRef.current?.clientSessionId
        ? sessionRef.current.clientSessionId
        : newSessionId();
      sessionRef.current = { clientSessionId, pageId: nextPage.pageId };
      pageRef.current = nextPage;
      lastTick = Date.now();
      send({
        event: resume ? 'resume' : 'start',
        resume,
        clientSessionId,
        pageId: nextPage.pageId,
        pageLabel: nextPage.pageLabel,
        module: nextPage.module,
        path: nextPage.path,
      });
    };

    const beat = () => {
      const sess = sessionRef.current;
      const current = pageRef.current;
      if (!sess || !current) return;
      const hiddenTooLong = hiddenAt && (Date.now() - hiddenAt) >= HIDDEN_GRACE_MS;
      if (hiddenTooLong) return;
      send({
        event: 'beat',
        clientSessionId: sess.clientSessionId,
        pageId: current.pageId,
        pageLabel: current.pageLabel,
        module: current.module,
        path: current.path,
      });
    };

    const endSession = () => {
      const sess = sessionRef.current;
      const current = pageRef.current;
      if (!sess || !current) return;
      send({
        event: 'end',
        clientSessionId: sess.clientSessionId,
        pageId: current.pageId,
        pageLabel: current.pageLabel,
        module: current.module,
        path: current.path,
      });
    };

    const pageChanged = pageRef.current?.pageId !== page.pageId;
    if (pageChanged) {
      if (sessionRef.current) endSession();
      startSession(page);
    } else if (!sessionRef.current) {
      startSession(page);
    }

    const onVisibility = () => {
      const sess = sessionRef.current;
      const current = pageRef.current;
      if (!sess || !current) return;
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        pendingMs += Date.now() - lastTick;
        lastTick = Date.now();
        send({
          event: 'beat',
          clientSessionId: sess.clientSessionId,
          pageId: current.pageId,
          pageLabel: current.pageLabel,
          module: current.module,
          path: current.path,
        });
      } else {
        const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
        hiddenAt = null;
        lastTick = Date.now();
        const graceSeconds = Math.min(Math.round(hiddenMs / 1000), HIDDEN_GRACE_MS / 1000);
        send({
          event: 'resume',
          resume: true,
          clientSessionId: sess.clientSessionId,
          pageId: current.pageId,
          pageLabel: current.pageLabel,
          module: current.module,
          path: current.path,
        }, { elapsedSeconds: graceSeconds });
      }
    };

    const interval = setInterval(beat, 30000);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', endSession);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', endSession);
      endSession();
      cancelled = true;
    };
  }, [accounts, instance, page?.pageId, page?.pageLabel, page?.module, page?.path]);
}
